// ShogiGame.jsx
// Bilgisayara karşı (4 zorluk) veya aynı cihazda iki kişilik şogi. Motor yalnızca yasal
// hamlelere izin verir; oyun, sırası gelenin yasal hamlesi kalmayınca biter (mat).
// Yapay zekâ hamlesini computeAiMove ile (mümkünse Web Worker'da) hesaplar.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import alertify from '../lib/alertifySetup';
import PhaserBoard from './PhaserBoard';
import HandRow from './HandRow';
import MoveHistory from './MoveHistory';
import useBoardInteraction from './useBoardInteraction';
import { Shogi, PIECE_NAMES, PROMOTED_NAMES } from '../lib/ShogiEngine';
import { computeAiMove, AI_LEVELS } from '../lib/ShogiAI';
import { useAuth } from '../context/AuthContext';
import { recordGameResult } from '../lib/gameStorage';
import { logAnalyticsEvent } from '../firebase';
import { useLang } from '../i18n';

const T = {
  tr: {
    level: 'Zorluk',
    levelHint: { easy: 'Yeni başlayan', medium: 'Kulüp oyuncusu', hard: 'Güçlü', expert: 'Çok güçlü' },
    opponent: 'Rakip',
    vsAi: 'Bilgisayar',
    vsHuman: 'Aynı cihazda 2 kişi',
    side: 'Tarafın',
    newGame: 'Yeni Oyun',
    undo: 'Geri Al',
    yourTurn: 'Sıra sende',
    turnOf: 'Sıra: {c}',
    thinking: 'Bilgisayar düşünüyor…',
    check: 'Şah!',
    you: 'Sen',
    cpu: 'Bilgisayar ({l})',
    win: 'Kazandın! 🎉',
    lose: 'Bilgisayar kazandı.',
    winner: '{c} kazandı!',
    reasonMate: 'Şah mat.',
    reasonNoMoves: 'Yasal hamle kalmadı.',
    notLogged: 'Giriş yapmadın — sonuç profiline ve liderlik tablosuna kaydedilmeyecek.',
    saved: 'Oyun profiline kaydedildi.',
    saveFail: 'Oyun kaydedilemedi: {e}',
    undoNote: 'Geri alma kullanıldığı için bu oyun puana sayılmayacak.',
    capture: '{a} rakibin {p} taşını aldı!',
    confirmNew: 'Devam eden oyun silinsin mi?',
  },
  en: {
    level: 'Difficulty',
    levelHint: { easy: 'Beginner', medium: 'Club player', hard: 'Strong', expert: 'Very strong' },
    opponent: 'Opponent',
    vsAi: 'Computer',
    vsHuman: '2 players, one device',
    side: 'Your side',
    newGame: 'New Game',
    undo: 'Undo',
    yourTurn: 'Your move',
    turnOf: 'To move: {c}',
    thinking: 'Computer is thinking…',
    check: 'Check!',
    you: 'You',
    cpu: 'Computer ({l})',
    win: 'You won! 🎉',
    lose: 'The computer won.',
    winner: '{c} wins!',
    reasonMate: 'Checkmate.',
    reasonNoMoves: 'No legal moves left.',
    notLogged: 'You are not signed in — this game will not be saved to your profile or the leaderboard.',
    saved: 'Game saved to your profile.',
    saveFail: 'Could not save the game: {e}',
    undoNote: 'Undo was used, so this game will not count for points.',
    capture: '{a} captured a {p}!',
    confirmNew: 'Discard the current game?',
  },
};

const LEVEL_KEY = 'sogi_ai_level';

function readLevel() {
  try {
    const v = localStorage.getItem(LEVEL_KEY);
    return AI_LEVELS.includes(v) ? v : 'easy';
  } catch {
    return 'easy';
  }
}

export default function ShogiGame({ onHistoryChange = () => {} }) {
  const { user } = useAuth();
  const { lang, t } = useLang();
  const tx = T[lang];

  const engineRef = useRef(null);
  if (!engineRef.current) engineRef.current = new Shogi();
  const engine = engineRef.current;

  const [level, setLevel] = useState(readLevel);
  const [vsAi, setVsAi] = useState(true);
  const [playerColor, setPlayerColor] = useState('sente');
  const [snapshot, setSnapshot] = useState({ board: engine.board(), lastMove: null, hands: engine.hands(), turn: engine.turn(), history: [] });
  const [thinking, setThinking] = useState(false);
  const [gameOver, setGameOver] = useState(null);
  const [undoUsed, setUndoUsed] = useState(false);
  const gameIdRef = useRef(0);

  const aiColor = playerColor === 'sente' ? 'gote' : 'sente';
  const turn = snapshot.turn;
  const isAiTurn = vsAi && turn === aiColor;
  const colorLabel = (c) => (c === 'sente' ? t('common.senteLong') : t('common.goteLong'));

  const sync = useCallback(
    (lastMove = null) => {
      setSnapshot({ board: engine.board(), lastMove, hands: engine.hands(), turn: engine.turn(), history: engine.history() });
      onHistoryChange(engine.history());
    },
    [engine, onHistoryChange]
  );

  const finish = useCallback(
    (winner, reason) => {
      setGameOver({ winner, reason });
      logAnalyticsEvent('game_complete', {
        winner, reason, move_count: engine.history().length, vs_ai: vsAi, level: vsAi ? level : 'human',
      });
      if (!vsAi) return;
      const result = winner === playerColor ? 'win' : 'loss';
      if (!user) return;
      if (undoUsed) {
        alertify.message(tx.undoNote);
        return;
      }
      recordGameResult(user.uid, {
        result,
        reason,
        history: engine.history(),
        opponentNickname: tx.cpu.replace('{l}', t(`level.${level}`)),
        aiLevel: level,
      })
        .then(() => alertify.success(tx.saved))
        .catch((e) => {
          console.error('Oyun kaydedilemedi:', e);
          alertify.error(tx.saveFail.replace('{e}', e.code || e.message || '?'));
        });
    },
    [engine, vsAi, level, playerColor, user, undoUsed, tx, t]
  );

  const afterMove = useCallback(
    (record) => {
      sync(record);
      if (record?.captured) {
        const actor = record.color === 'sente' ? t('common.sente') : t('common.gote');
        alertify.warning(tx.capture.replace('{a}', actor).replace('{p}', PIECE_NAMES[record.captured] || record.captured));
      }
      const next = engine.turn();
      if (!engine.hasAnyLegalMoves(next)) {
        finish(record.color, engine.isCheck(next) ? 'checkmate' : 'no-moves');
      } else if (engine.isCheck(next)) {
        alertify.warning(tx.check);
      }
    },
    [engine, sync, finish, tx, t]
  );

  const onMove = useCallback(
    (descriptor) => {
      const record = engine.applyDescriptor(descriptor);
      afterMove(record);
    },
    [engine, afterMove]
  );

  const humanColor = vsAi ? playerColor : turn;
  const interaction = useBoardInteraction(engine, {
    color: humanColor,
    canAct: !gameOver && !thinking && !isAiTurn,
    onMove,
  });

  // Yapay zekâ hamlesi
  useEffect(() => {
    if (gameOver || !isAiTurn) return undefined;
    const myGame = gameIdRef.current;
    let cancelled = false;
    setThinking(true);
    const started = Date.now();
    computeAiMove(engine, { level })
      .then((desc) => {
        // Çok hızlı hamleler doğal görünsün diye en az ~500 ms bekle
        const wait = Math.max(0, 500 - (Date.now() - started));
        setTimeout(() => {
          if (cancelled || myGame !== gameIdRef.current) return;
          setThinking(false);
          if (!desc) {
            finish(playerColor, 'no-moves');
            return;
          }
          afterMove(engine.applyDescriptor(desc));
        }, wait);
      })
      .catch((e) => {
        console.error('AI hatası:', e);
        if (!cancelled) setThinking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAiTurn, gameOver, engine, level, afterMove, finish, playerColor]);

  const startNew = useCallback(
    (opts = {}) => {
      gameIdRef.current += 1;
      engine.reset();
      interaction.clear();
      setThinking(false);
      setGameOver(null);
      setUndoUsed(false);
      if (opts.playerColor) setPlayerColor(opts.playerColor);
      if (opts.vsAi !== undefined) setVsAi(opts.vsAi);
      sync(null);
      logAnalyticsEvent('game_start', { level: (opts.vsAi ?? vsAi) ? level : 'human' });
    },
    [engine, interaction, sync, level, vsAi]
  );

  const confirmThen = (fn) => {
    if (engine.history().length === 0 || gameOver) {
      fn();
      return;
    }
    alertify
      .confirm(tx.newGame, tx.confirmNew, fn, () => {})
      .set('labels', { ok: t('common.yes'), cancel: t('common.no') });
  };

  const changeLevel = (lv) => {
    if (lv === level) return;
    confirmThen(() => {
      setLevel(lv);
      try { localStorage.setItem(LEVEL_KEY, lv); } catch { /* yok say */ }
      startNew();
    });
  };

  const canUndo = typeof engine.undo === 'function' && !thinking && !gameOver && engine.history().length > 0;
  const handleUndo = () => {
    if (!canUndo) return;
    gameIdRef.current += 1;
    engine.undo();
    // Bilgisayara karşı: sıra tekrar oyuncuya gelene kadar geri al
    if (vsAi && engine.turn() !== playerColor && engine.history().length > 0) engine.undo();
    interaction.clear();
    setUndoUsed(true);
    sync(null);
  };

  const bottomColor = vsAi ? playerColor : 'sente';
  const topColor = bottomColor === 'sente' ? 'gote' : 'sente';
  const sideName = (c) => {
    if (!vsAi) return colorLabel(c);
    return c === playerColor ? `${tx.you} · ${colorLabel(c)}` : tx.cpu.replace('{l}', t(`level.${level}`));
  };
  const inCheck = !gameOver && engine.isCheck(turn);

  let status;
  if (gameOver) status = null;
  else if (thinking) status = <span className="status-pill"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> {tx.thinking}</span>;
  else if (vsAi) status = <span className={`status-pill turn-${turn}`}>{tx.yourTurn}</span>;
  else status = <span className={`status-pill turn-${turn}`}>{tx.turnOf.replace('{c}', colorLabel(turn))}</span>;

  return (
    <div className="row g-3 g-lg-4">
      <div className="col-12 col-lg-8">
        <div className="card">
          <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {status}
              {inCheck && <span className="status-pill check">{tx.check}</span>}
            </div>
            <div className="d-flex gap-2">
              {typeof engine.undo === 'function' && (
                <button type="button" className="btn btn-soft btn-sm" onClick={handleUndo} disabled={!canUndo}>↶ {tx.undo}</button>
              )}
              <button type="button" className="btn btn-ink btn-sm" onClick={() => confirmThen(() => startNew())}>{tx.newGame}</button>
            </div>
          </div>
          {!user && vsAi && <div className="small text-muted text-center border-bottom py-1 px-2">{tx.notLogged}</div>}
          <div className="card-body d-flex flex-column gap-2 p-2 p-sm-3">
            {gameOver && (
              <div className="alert alert-success text-center mb-1 py-2">
                <div className="fw-bold fs-5">
                  {vsAi ? (gameOver.winner === playerColor ? tx.win : tx.lose) : tx.winner.replace('{c}', colorLabel(gameOver.winner))}
                </div>
                <div className="small mb-2">{gameOver.reason === 'checkmate' ? tx.reasonMate : tx.reasonNoMoves}</div>
                <button type="button" className="btn btn-sm btn-ink" onClick={() => startNew()}>{tx.newGame}</button>
              </div>
            )}

            <HandRow
              label={sideName(topColor)}
              hand={snapshot.hands[topColor]}
              active={!vsAi && turn === topColor && !gameOver}
              dropType={interaction.dropType}
              onPick={interaction.handleHandClick}
            />

            <div className="board-wrap">
              <PhaserBoard
                rows={9}
                cols={9}
                cellSize={64}
                board={snapshot.board}
                lastMove={snapshot.lastMove}
                pieceLabels={PIECE_NAMES}
                promotedLabels={PROMOTED_NAMES}
                selected={interaction.selected}
                legalTargets={interaction.legalTargets}
                onSquareClick={interaction.handleSquareClick}
                flipBoard={bottomColor === 'gote'}
              />
            </div>

            <HandRow
              label={sideName(bottomColor)}
              hand={snapshot.hands[bottomColor]}
              active={!gameOver && !thinking && turn === bottomColor && (!vsAi || bottomColor === playerColor)}
              dropType={interaction.dropType}
              onPick={interaction.handleHandClick}
            />
          </div>
        </div>
      </div>

      <div className="col-12 col-lg-4">
        <div className="panel mb-3">
          <div className="fw-bold mb-2">{tx.opponent}</div>
          <div className="chip-row mb-3">
            <button type="button" className={`chip ${vsAi ? 'active' : ''}`} style={{ '--c': 'var(--c-play)' }} onClick={() => !vsAi && confirmThen(() => startNew({ vsAi: true }))}>{tx.vsAi}</button>
            <button type="button" className={`chip ${!vsAi ? 'active' : ''}`} style={{ '--c': 'var(--c-arena)' }} onClick={() => vsAi && confirmThen(() => startNew({ vsAi: false }))}>{tx.vsHuman}</button>
          </div>

          {vsAi && (
            <>
              <div className="fw-bold mb-2">{tx.level}</div>
              <div className="level-picker mb-3" role="radiogroup" aria-label={tx.level}>
                {AI_LEVELS.map((lv) => (
                  <button key={lv} type="button" role="radio" aria-checked={lv === level} className={lv === level ? 'active' : ''} onClick={() => changeLevel(lv)}>
                    {t(`level.${lv}`)}
                    <small>{tx.levelHint[lv]}</small>
                  </button>
                ))}
              </div>

              <div className="fw-bold mb-2">{tx.side}</div>
              <div className="chip-row">
                {['sente', 'gote'].map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`chip ${playerColor === c ? 'active' : ''}`}
                    style={{ '--c': c === 'sente' ? 'var(--ink)' : 'var(--red)' }}
                    onClick={() => playerColor !== c && confirmThen(() => startNew({ playerColor: c }))}
                  >
                    {colorLabel(c)}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <MoveHistory history={snapshot.history} />
      </div>
    </div>
  );
}
